import {Phone, Calendar, Palette, Sparkles, Check, MapPin, Award, Shield} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'

const CarWrapSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    // Add FAQ Schema
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does a car wrap cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial wraps start at $1,500-$2,500. Full color change wraps range from $3,500-$6,500 depending on vehicle size and vinyl quality. Custom designs and complex installations may cost more."
          }
        },
        {
          "@type": "Question",
          "name": "How long does a vinyl wrap last?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Premium vinyl wraps last 5-7 years in San Diego with proper care. Durability depends on sun exposure, maintenance, and vinyl quality. Cast vinyl lasts longer than calendared vinyl."
          }
        },
        {
          "@type": "Question",
          "name": "Will a vinyl wrap damage my car's paint?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Professional-grade vinyl protects your factory paint and removes cleanly when properly installed and removed. It actually preserves paint condition underneath."
          }
        },
        {
          "@type": "Question",
          "name": "How long does car wrap installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial wraps: 1-2 days. Full color change wraps: 3-5 days. Custom designs with graphics: 5-7 days depending on complexity."
          }
        },
        {
          "@type": "Question",
          "name": "Can I wrap a leased vehicle?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Vinyl wraps are fully removable and actually protect the factory paint underneath, making them ideal for leased vehicles. Remove before lease return or keep for added protection."
          }
        },
        {
          "@type": "Question",
          "name": "What colors and finishes are available?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We offer 500+ colors including gloss, matte, satin, metallic, chrome, brushed metal, color-flip, and textured finishes. Custom Pantone color matching available."
          }
        }
      ]
    })
    document.head.appendChild(script)

    // Set meta tags
    document.title = "Car Wrap San Diego | Vinyl & Color Change Wraps | Mr Wrap"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional car wraps in Vista, North County San Diego. 13 years of experience with premium vinyl, full color-change wraps, and custom vehicle wraps. Request a quote.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/car-wrap-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Car Wrap San Diego - Professional Vehicle Vinyl Wraps & Color Change</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Transform your vehicle with professional car wrapping services in San Diego. From full vehicle wraps and color-change vinyl to custom vehicle wraps, our installers use premium films, thorough preparation, and careful edge work to create a clean new look while helping protect factory paint.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Mr Wrap has 13 years of professional installation experience at our Vista shop, serving North County and greater San Diego County — including Vista, Carlsbad, Oceanside, Encinitas, and surrounding areas. We install luxury vehicle wraps, commercial fleet graphics, and custom designs with 500+ color options in gloss, matte, satin, metallic, chrome, and specialty finishes.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Wrap Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-950 border-y border-cyan-500/20 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                <Award className="text-cyan-400" size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">13 Years Experience</p>
                <p className="text-gray-400 text-xs">Professional installation</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                <Shield className="text-cyan-400" size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Premium Films</p>
                <p className="text-gray-400 text-xs">Quality vinyl materials</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                <Sparkles className="text-cyan-400" size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Professional Installation</p>
                <p className="text-gray-400 text-xs">Prep, edges & finish work</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                <MapPin className="text-cyan-400" size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Vista, CA</p>
                <p className="text-gray-400 text-xs">North County San Diego</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white text-center">Looking for Car Wraps Near Me in San Diego?</h2>
          <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
            <p>
              Mr Wrap installs professional car wraps from our shop in Vista and serves North County plus greater San Diego County. If you are comparing local wrapping services for a full color-change wrap, a vinyl wrap, or a custom vehicle wrap, you can visit the shop, review finish samples, and get a quote based on your actual vehicle.
            </p>
            <p>
              Drivers come to us from Vista, Oceanside, Carlsbad, San Marcos, Encinitas, and across San Diego County for indoor installation, thorough vehicle preparation, and careful edge work. We handle full vehicle wraps as well as partial coverage when a roof, hood, or accent wrap is a better fit. With 13 years of professional installation experience, we use premium vinyl and choose the right method for each panel — including disassembly and tucked edges where appropriate.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-white text-center">Completed Vinyl Wrap Projects</h2>
          <p className="text-gray-400 text-center max-w-3xl mx-auto mb-12">
            Real vinyl wrap installations completed at our Vista shop. These photos are also shown elsewhere on the site and are reused here so you can see finished wrap work on this page.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <article className="bg-gray-900 rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="grid grid-cols-2 gap-1">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://static.lumi.new/8e/8e4dce06736b5bb85023e6533325df91.webp"
                    alt="Porsche Taycan with vinyl color-change wrap, front view"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={600}
                    height={450}
                  />
                </div>
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://static.lumi.new/75/75d8f2ed5fe887c116007e0c6ed95918.webp"
                    alt="Porsche Taycan with vinyl color-change wrap, side profile"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={600}
                    height={450}
                  />
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Vinyl Color-Change Wrap</p>
                <h3 className="text-xl font-bold text-white mb-2">Porsche Taycan</h3>
                <p className="text-gray-300 text-sm">Vinyl color-change wrap with ceramic coating. The wrap customizes the vehicle's appearance and is not the same as paint protection film.</p>
              </div>
            </article>
            <article className="bg-gray-900 rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.lumi.new/c7/c78cb81a446620a625d8454e3919a7cd.webp"
                  alt="BMW M3 after Inozetek Laguna Blue vinyl wrap"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={900}
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Full Color Change Wrap</p>
                <h3 className="text-xl font-bold text-white mb-2">BMW M3</h3>
                <p className="text-gray-300 text-sm">Inozetek Laguna Blue vinyl wrap with gloss black chrome delete and caliper painting, completed as a full color-change wrap at Mr Wrap.</p>
              </div>
            </article>
            <article className="bg-gray-900 rounded-2xl overflow-hidden border border-cyan-500/20 flex flex-col">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://static.lumi.new/f9/f91481cd9df0c935c21521a7fa91a7f9.webp"
                  alt="White Lamborghini with vinyl color-change wrap"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1200}
                  height={900}
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide mb-2">Vinyl Color-Change Wrap</p>
                <h3 className="text-xl font-bold text-white mb-2">Lamborghini</h3>
                <p className="text-gray-300 text-sm">Premium vinyl color-change wrap for a complete exterior transformation. Vinyl wrap changes appearance and is not the same as paint protection film.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Vinyl Wraps in San Diego</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              <a href="/vinyl-wraps" className="text-cyan-400 hover:underline">Vinyl vehicle wraps</a> offer extensive customization while helping protect factory paint. Unlike a traditional paint job, a vinyl wrap is fully reversible and can usually be completed in days instead of weeks. Change the entire appearance of a vehicle — from a subtle matte black full vehicle wrap to a bold chrome or custom graphic — without a permanent color change.
            </p>
            <p>
              San Diego's car culture and year-round weather make vehicle wraps a popular alternative to paint. From luxury sedans in <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a> to daily drivers and work vehicles throughout North County, vinyl wraps deliver a new look plus practical UV and surface protection. For a detailed breakdown of what affects pricing, see our <a href="/vehicle-wrap-cost-san-diego" className="text-cyan-400 hover:underline">vehicle wrap cost guide for San Diego</a>.
            </p>
            <p>
              Installation includes thorough cleaning, professional application, and post-heating for lasting adhesion. We wrap compact cars, full-size trucks, sports cars, and commercial vehicles using a mix of precision patterns and careful hand work, with panel disassembly and tucked edges where appropriate. Combine your wrap with <a href="/window-tinting" className="text-cyan-400 hover:underline">ceramic window tint</a> and <a href="/chrome-delete" className="text-cyan-400 hover:underline">chrome delete</a> for a complete transformation in one visit to our <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista shop</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Car Wrap Pricing in San Diego</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Wrap</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$1,500 - $2,500</div>
              <p className="text-gray-400 text-sm mb-6">Roof, hood, or accent panels</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof wrap (panoramic glass accent)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Hood and mirror wraps</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Racing stripes and accents</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>500+ color options</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Color Change</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$3,500 - $6,500</div>
              <p className="text-gray-400 text-sm mb-6">Complete vehicle transformation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All exterior painted surfaces</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Door jambs and edges</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Premium cast vinyl (5-7 year life)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer warranty included</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Custom Design Wrap</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">$5,000 - $12,000+</div>
              <p className="text-gray-400 text-sm mb-6">Complex graphics and branding</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Custom graphic design</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Multi-color complex layouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Commercial fleet branding</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Show car quality finish</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400">
            All packages include design consultation, professional installation, and the applicable film manufacturer warranty. <a href="/commercial-fleet-wraps" className="text-cyan-400 hover:underline">Fleet discounts</a> available. Starting prices shown above are based on vehicle size and wrap complexity. Exact pricing will be determined after a free consultation and vehicle inspection. For typical ranges by vehicle type, review our <a href="/vehicle-wrap-cost-san-diego" className="text-cyan-400 hover:underline">San Diego vehicle wrap cost guide</a>.
          </p>
        </div>
      </section>

      {/* Vinyl Options */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Vinyl Wrap Finishes & Colors</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Gloss Finishes</h3>
              <p className="text-gray-300">High-gloss vinyl mimics factory paint with deep reflective shine. Perfect for color changes maintaining a premium OEM appearance. Available in 100+ colors.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Palette className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Matte Finishes</h3>
              <p className="text-gray-300">Non-reflective matte vinyl creates a sophisticated, stealthy aesthetic. Popular colors: matte black, military green, charcoal gray, deep blue. Requires special care products.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Satin Finishes</h3>
              <p className="text-gray-300">Subtle semi-gloss finish blending matte and gloss characteristics. Hides minor imperfections while maintaining premium appearance. Easy maintenance.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Chrome & Metallic</h3>
              <p className="text-gray-300">Eye-catching chrome, brushed metal, and metallic finishes for maximum impact. Popular for exotic vehicles, show cars, and bold personal expressions.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Palette className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Color Flip & Specialty</h3>
              <p className="text-gray-300">Advanced color-shifting vinyl changes appearance based on viewing angle and lighting. Creates unique, one-of-a-kind looks impossible with traditional paint.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Textured Finishes</h3>
              <p className="text-gray-300">Carbon fiber, brushed aluminum, leather, and other textured vinyl options add dimensional interest and premium tactile feel to your vehicle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Car Wrap Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Design Consultation & Color Selection</h3>
                <p className="text-gray-300">We start with an in-person consultation at our Vista facility where you can view physical vinyl samples, browse our 500+ color catalog, and visualize your vehicle transformation. We'll discuss your style preferences, usage requirements, and budget to recommend the ideal vinyl type and finish. For custom designs, our graphic designers create digital mockups for your approval before production.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Vehicle Preparation & Paint Inspection</h3>
                <p className="text-gray-300">Thorough vehicle cleaning and paint inspection ensures optimal vinyl adhesion. We wash, clay bar, and degrease all surfaces using professional automotive detailing products. Any existing paint defects are documented and addressed. For maximum longevity, we may recommend light paint correction to create a perfectly smooth surface for the vinyl application.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Professional Wrap Installation</h3>
                <p className="text-gray-300">Our installers apply premium vinyl using thorough prep, panel disassembly where appropriate, precision patterns or bulk installation as needed, stretching, and post-heating for lasting adhesion. We work indoors to support consistent results. Complex curves, recessed areas, and body lines receive careful attention. Door jambs, edges, and trim pieces are wrapped or tucked where appropriate for a clean, professional finish.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Inspection & Care Instructions</h3>
                <p className="text-gray-300">Final quality inspection checks application quality, edge sealing, and coverage. We provide care instructions including cleaning products to use, what to avoid, and maintenance tips for maximizing wrap longevity. You'll receive applicable film manufacturer warranty documentation and access to our team for questions after installation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Car Wrap FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does a car wrap cost in San Diego?</h3>
              <p className="text-gray-300">Partial wraps (roof, hood, accents) start at $1,500-$2,500. Full color change wraps range from $3,500-$6,500 depending on vehicle size and vinyl quality. Custom designs with graphics range from $5,000-$12,000+ for complex commercial fleet branding. Pricing depends on vehicle size, vinyl selection (gloss, matte, chrome), and design complexity. We provide exact quotes after inspecting your vehicle. Compare typical ranges in our <a href="/vehicle-wrap-cost-san-diego" className="text-cyan-400 hover:underline">vehicle wrap cost guide</a>.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does a vinyl wrap last?</h3>
              <p className="text-gray-300">Premium cast vinyl wraps last 5-7 years in San Diego with proper care. Durability depends on sun exposure, maintenance, and vinyl quality. Garaged vehicles and those with regular maintenance last longest. Cheaper calendared vinyl (not recommended) lasts only 3-5 years. Our wraps use premium 3M, Avery Dennison, and other top-tier materials designed for California's intense UV environment.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will a vinyl wrap damage my car's paint?</h3>
              <p className="text-gray-300">No. Professional-grade automotive vinyl uses pressure-sensitive adhesive designed specifically for automotive paint. When properly installed and removed by professionals, vinyl protects your factory paint and removes cleanly without damage. In fact, wrapped paint is often in better condition than unwrapped areas because it's protected from UV rays, oxidation, and environmental contaminants for years.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does car wrap installation take?</h3>
              <p className="text-gray-300">Partial wraps: 1-2 days. Full color change wraps: 3-5 days. Custom designs with complex graphics: 5-7 days depending on design intricacy and vehicle size. We never rush installation—proper panel preparation, vinyl application, and post-heating ensure your wrap lasts the full 5-7 year lifespan. Quality work takes time.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I wrap a leased vehicle?</h3>
              <p className="text-gray-300">Yes. Vinyl wraps are ideal for leased vehicles because they're fully removable and actually protect the factory paint underneath. Many lessees wrap their vehicles for personalization, then remove the wrap before lease return—revealing pristine factory paint. Wraps can also be kept for the full lease term for added paint protection.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What colors and finishes are available?</h3>
              <p className="text-gray-300">We offer 500+ colors and finishes including: Gloss (high-shine factory look), Matte (flat non-reflective), Satin (subtle semi-gloss), Metallic (sparkle effect), Chrome (mirror finish), Brushed Metal (textured aluminum), Carbon Fiber (3D texture), Color Flip (changes with viewing angle), and custom Pantone color matching for exact brand colors.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I wash my car normally after wrapping?</h3>
              <p className="text-gray-300">Yes. After 7 days curing, wrapped vehicles can be hand washed or run through touchless automatic washes. Avoid brush car washes, high-pressure spray directly on edges, and harsh chemicals. Use pH-neutral soap and microfiber towels. Matte wraps require specific matte-safe cleaning products to maintain finish.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Do you wrap commercial vehicles and fleets?</h3>
              <p className="text-gray-300">Yes. Dedicated <a href="/commercial-fleet-wraps" className="text-cyan-400 hover:underline">fleet and commercial wrap services</a> cover vans, trucks, trailers, and business vehicles with custom graphics, branding, and contact information. Fleet wraps turn vehicles into mobile advertising. Volume discounts are available for multiple vehicles.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can vinyl wraps be combined with PPF?</h3>
              <p className="text-gray-300">Yes. Many clients combine color change wraps with <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">paint protection film</a> over high-impact areas (front bumper, hood, mirrors) for ultimate protection. We also offer full-vehicle PPF in colored or textured finishes as an alternative to vinyl—providing both protection and aesthetics in one premium material.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Where is your wrap shop located in San Diego?</h3>
              <p className="text-gray-300">We're located at 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084. Easily accessible from <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a>, <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a>, <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a>, <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a>, and throughout North County San Diego. Free consultations with vinyl samples available by appointment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Quote Form */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmbeddedQuoteForm
            defaultService="vehicle-wrap"
            heading="Get a Free Car Wrap Quote"
            subheading="Tell us about your vehicle, your color ideas, and your goals. Our Vista installers will reach out with an honest, no-pressure estimate."
          />
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Explore our full range of vinyl wrap and customization services:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <a href="/vehicle-wrap-cost-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Vehicle Wrap Cost San Diego</h3>
              <p className="text-gray-400 text-sm">See what affects wrap pricing, typical ranges, and how vinyl compares with Colored PPF</p>
            </a>
            <a href="/tesla-wrap-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla Wrap San Diego</h3>
              <p className="text-gray-400 text-sm">Professional Tesla wrap services for Model 3, Model Y, Model S, and Model X with expert installation</p>
            </a>
            <a href="/colored-ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Colored PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Color change paint protection film with self-healing protection in gloss, satin, matte, and stealth finishes</p>
            </a>
            <a href="/chrome-delete" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete</h3>
              <p className="text-gray-400 text-sm">Remove chrome trim and accents with vinyl wrap for a sleek, modern appearance</p>
            </a>
            <a href="/window-tinting" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Window Tinting</h3>
              <p className="text-gray-400 text-sm">Ceramic window tint installation for UV protection, heat reduction, and enhanced privacy</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Car Wrap Installation Serving All of San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Based in <a href="/ppf-vista" className="text-cyan-400 hover:underline font-semibold">Vista</a>, we provide professional vinyl wrap installation throughout San Diego County and surrounding areas.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Coastal</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a></li>
                <li>• <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a></li>
                <li>• <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a></li>
                <li>• Solana Beach</li>
                <li>• Del Mar</li>
              </ul>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Inland</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> (Shop Location)</li>
                <li>• <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a></li>
                <li>• San Marcos</li>
                <li>• Escondido</li>
                <li>• Poway</li>
                <li>• Ramona</li>
              </ul>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Greater San Diego</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">San Diego</a></li>
                <li>• La Jolla</li>
                <li>• Mission Valley</li>
                <li>• Downtown SD</li>
                <li>• Chula Vista</li>
                <li>• All SD County</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Transform Your Vehicle with a Professional Car Wrap</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free wrap consultation and explore 500+ colors and finishes. Same-week appointments available for color changes and custom designs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Wrap Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default CarWrapSanDiegoPage
